/**
 * Fair Settlement Calculator Section
 * Monte Carlo-based mediation analysis between adjuster and attorney positions
 * 
 * Purpose: Provide a statistically fair settlement value that bridges
 * the gap between conservative (adjuster) and aggressive (attorney) positions.
 * 
 * Features:
 * - Settlement range visualization (Low → Fair → High)
 * - Monte Carlo statistics with confidence intervals
 * - Gap bridge analysis ("If you fix X, claim increases by $Y")
 * - Settlement scenarios with recommendations
 */

import React, { useState, useEffect } from 'react';
import axios from '@/lib/http';
import { getAuthHeaders } from '../../lib/authHeaders';
import {
  Scale,
  TrendingUp, 
  DollarSign, 
  Target,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  ArrowRight,
  Info,
  BarChart3
} from 'lucide-react';
import { formatMoneyOrNotComputed, NOT_COMPUTED_TEXT } from '../../utils/financialFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface FairSettlementProps {
  reportId: string;
  getAuthHeader: () => { Authorization: string } | {};
  formatCurrency: (amount?: number) => string;
}

interface SettlementRange {
  conservative: number;
  fair: number;
  aggressive: number;
}

interface MonteCarloStats {
  iterations: number;
  mean: number;
  median: number;
  std_dev: number;
  p10: number;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  min: number;
  max: number;
  distribution_sample: number[];
}

interface GapBridgeItem {
  gap_id: number;
  title: string;
  description: string;
  severity?: string;
  category?: string;
  confidence?: string;
  current_impact: number;
  if_addressed: {
    value_increase: number;
    new_claim_value: number;
    percentage_increase: number;
  };
  how_to_address: string;
  evidence_needed?: string[];
  source: string;
  cumulative_claim: number;
}

interface Scenario {
  name: string;
  value: number;
  description: string;
  position: string;
  color: string;
  recommended?: boolean;
}

interface FairSettlementAnalysis {
  success: boolean;
  cached: boolean;
  analysis_id?: string;
  analyzed_at?: string;
  fair_settlement_value?: number;
  settlement_range?: SettlementRange;
  confidence_interval_95?: { low: number; high: number };
  adjuster_value?: number;
  attorney_value?: number;
  base_claim?: number;
  monte_carlo?: MonteCarloStats;
  gap_bridge?: GapBridgeItem[];
  scenarios?: Scenario[];
  executive_summary?: string;
  error?: string;
}

export default function FairSettlementSection({
  reportId,
  getAuthHeader,
  formatCurrency
}: FairSettlementProps) {
  const [analysis, setAnalysis] = useState<FairSettlementAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    scenarios: true,
    gapBridge: false,
    monteCarlo: false
  });

  const fetchAnalysis = async (forceRefresh = false) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/v1/mediation/calculate/${reportId}?force_refresh=${forceRefresh}`,
        {},
        { headers: await getAuthHeaders() }
      );
      setAnalysis(response.data);
    } catch (err: any) {
      console.error('Fair settlement calculation failed:', err);
      setError(err.response?.data?.detail || 'Failed to calculate fair settlement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalysis();
  }, [reportId]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getScenarioColorStyle = (color: string): React.CSSProperties => {
    const brandStyle: React.CSSProperties = {
      backgroundColor: 'var(--color-bg-elevated)',
      borderColor: 'var(--color-border)',
      color: 'var(--color-text-primary)',
    };
    const doneStyle: React.CSSProperties = {
      backgroundColor: 'transparent',
      borderColor: 'var(--color-verified)',
      color: 'var(--color-verified)',
    };
    const colorMap: Record<string, React.CSSProperties> = {
      red: brandStyle,
      amber: brandStyle,
      green: doneStyle,
      blue: brandStyle,
      purple: brandStyle,
      emerald: doneStyle
    };
    return colorMap[color] || {
      backgroundColor: 'var(--color-bg-elevated)',
      borderColor: 'var(--color-border)',
      color: 'var(--color-text-primary)',
    };
  };

  if (loading && !analysis) {
    return (
      <div className="rounded-md border border-[var(--color-border)] p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Fair Settlement Calculator</h2>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-10 w-10 mb-4" style={{ borderBottom: '2px solid var(--color-brand)' }}></div>
            <p style={{ color: 'var(--color-text-secondary)' }}>Running Monte Carlo simulation...</p>
            <p className="text-sm mt-1" style={{ color: 'var(--color-text-muted)' }}>2,000+ iterations for statistical accuracy</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-[var(--color-border)] p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        <div className="flex items-center gap-3 mb-4">
          <Scale className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
          <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Fair Settlement Calculator</h2>
        </div>
        <div className="rounded-md border border-[var(--color-border)] p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
          <p style={{ color: 'var(--color-text-secondary)' }}>{error}</p>
          <button
            onClick={() => fetchAnalysis(true)}
            className="mt-3 px-4 py-2 rounded-md text-sm"
            style={{ backgroundColor: 'transparent', color: 'var(--color-brand)', border: '1px solid var(--color-border)' }}
          >
            Retry Calculation
          </button>
        </div>
      </div>
    );
  }

  if (!analysis) return null;

  const {
    fair_settlement_value,
    settlement_range,
    confidence_interval_95,
    adjuster_value,
    attorney_value,
    base_claim,
    monte_carlo,
    gap_bridge,
    scenarios,
    executive_summary,
    cached,
    analyzed_at
  } = analysis;

  // Calculate position percentage for the fair settlement on the scale
  const getPositionPercent = () => {
    if (!adjuster_value || !attorney_value || !fair_settlement_value) return 50;
    const range = attorney_value - adjuster_value;
    if (range === 0) return 50;
    return Math.min(100, Math.max(0, ((fair_settlement_value - adjuster_value) / range) * 100));
  };

  // Calculate total gap value
  const totalGapValue = gap_bridge?.reduce((sum, gap) => sum + gap.if_addressed.value_increase, 0) || 0;

  return (
    <div className="rounded-md border border-[var(--color-border)] p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-surface)' }} data-testid="fair-settlement-section">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
            <Scale className="w-6 h-6" style={{ color: 'var(--color-needs-human-text)' }} />
          </div>
          <div>
            <h2 className="cs-h2" style={{ color: 'var(--color-text-primary)' }}>Fair Settlement Calculator</h2>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
              Monte Carlo Analysis • {cached ? '(Cached)' : 'Fresh'} • {analyzed_at ? new Date(analyzed_at).toLocaleString() : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => fetchAnalysis(true)}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 rounded-md transition"
          style={{ color: 'var(--color-needs-human-text)' }}
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Recalculate
        </button>
      </div>

      {/* Executive Summary */}
      <div className="rounded-md border border-[var(--color-border)] p-6 mb-6" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
        <div className="flex items-start gap-4 mb-6">
          <div className="flex-1">
            <h3 className="cs-h3 mb-2" style={{ color: 'var(--color-text-primary)' }}>Statistical Fair Settlement</h3>
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{executive_summary}</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(fair_settlement_value)}</p>
            <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>Recommended (P50)</p>
            {/* CT-P scope label (KAIROS 1386): the fair-settlement figure is the
                business-interruption supported net (policy_deductions.net_claim); a
                first-party PROPERTY claim (L_PROP / RCV) is a SEPARATE track and is NOT
                in this number. Prevents a reader quoting the BI settlement as the whole
                case when it sits next to the reconstructed-indemnity total. */}
            <p className="text-xs mt-0.5 max-w-[16rem] ml-auto" style={{ color: 'var(--color-text-dim)' }}>
              Business-interruption supported net — any first-party property claim is valued separately.
            </p>
            {monte_carlo && (
              <p className="text-xs mt-1" style={{ color: 'var(--color-text-dim)' }}>
                {monte_carlo.iterations.toLocaleString()} iterations
              </p>
            )}
          </div>
        </div>

        {/* Settlement Scale Visualization */}
        <div className="rounded-md border border-[var(--color-border)] p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
          <p className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>Settlement Range</p>

          {/* Scale Bar */}
          <div className="relative h-8 mb-2">
            {/* Background track */}
            <div className="absolute inset-0 rounded-full" style={{ backgroundColor: 'var(--color-bg-elevated)' }}></div>

            {/* Fair Settlement Marker */}
            <div
              className="absolute top-0 h-full flex flex-col items-center transition-all duration-500"
              style={{ left: `${getPositionPercent()}%`, transform: 'translateX(-50%)' }}
            >
              <div className="w-1 h-full rounded" style={{ backgroundColor: 'var(--color-brand)' }}></div>
              <div className="absolute -top-6 text-xs px-2 py-1 rounded font-semibold whitespace-nowrap font-mono tabular-nums" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-brand)', border: '1px solid var(--color-brand)' }}>
                Fair: {formatCurrency(fair_settlement_value)}
              </div>
            </div>
          </div>

          {/* Labels */}
          <div className="flex justify-between text-xs">
            <div style={{ color: 'var(--color-needs-human-text)' }}>
              <p className="font-semibold">Adjuster (Low)</p>
              <p>{formatCurrency(adjuster_value)}</p>
            </div>
            <div className="text-center" style={{ color: 'var(--color-text-secondary)' }}>
              <p className="font-semibold">Base Claim</p>
              <p>{formatCurrency(base_claim)}</p>
            </div>
            <div className="text-right" style={{ color: 'var(--color-verified)' }}>
              <p className="font-semibold">Attorney (High)</p>
              <p>{formatCurrency(attorney_value)}</p>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-4 gap-4 mt-4">
          <div className="text-center rounded-md border border-[var(--color-border)] p-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Conservative (P25)</p>
            <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(settlement_range?.conservative)}</p>
          </div>
          <div className="text-center rounded-md border border-[var(--color-border)] p-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Fair (P50)</p>
            <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(settlement_range?.fair)}</p>
          </div>
          <div className="text-center rounded-md border border-[var(--color-border)] p-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Aggressive (P75)</p>
            <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(settlement_range?.aggressive)}</p>
          </div>
          <div className="text-center rounded-md border border-[var(--color-border)] p-3" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
            <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>95% CI Range</p>
            <p className="text-sm font-bold font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
              {formatCurrency(confidence_interval_95?.low)} - {formatCurrency(confidence_interval_95?.high)}
            </p>
          </div>
        </div>
      </div>

      {/* Settlement Scenarios */}
      {scenarios && scenarios.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('scenarios')}
            className="w-full flex items-center justify-between p-4 rounded-md border border-[var(--color-border)] transition"
            style={{ backgroundColor: 'var(--color-bg-elevated)' }}
          >
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>Settlement Scenarios</span>
            </div>
            {expandedSections.scenarios ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.scenarios && (
            <div className="mt-3 space-y-3">
              {scenarios.map((scenario, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-md border"
                  style={getScenarioColorStyle(scenario.color)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold">{scenario.name}</h4>
                        {scenario.recommended && (
                          <span className="px-2 py-0.5 text-xs rounded-full font-semibold" style={{ backgroundColor: 'transparent', color: 'var(--color-brand)', border: '1px solid var(--color-brand)' }}>
                            RECOMMENDED
                          </span>
                        )}
                      </div>
                      <p className="text-sm opacity-80">{scenario.description}</p>
                    </div>
                    <p className="text-xl font-bold font-mono tabular-nums ml-4">{formatCurrency(scenario.value)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Gap Bridge Analysis */}
      {gap_bridge && gap_bridge.length > 0 && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('gapBridge')}
            className="w-full flex items-center justify-between p-4 rounded-md transition"
            style={{ backgroundColor: 'transparent' }}
          >
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-verified)' }}>
                Gap Bridge Analysis: {formatCurrency(totalGapValue)} Potential Recovery
              </span>
            </div>
            {expandedSections.gapBridge ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.gapBridge && (
            <div className="mt-3 space-y-4">
              <p className="text-sm px-2" style={{ color: 'var(--color-text-secondary)' }}>
                Address these gaps to increase claim value. Shows cumulative impact.
              </p>

              {gap_bridge.map((gap, idx) => (
                <div
                  key={idx}
                  className="p-4 rounded-md transition"
                  style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-verified)' }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>
                        {gap.gap_id}
                      </div>
                      <div>
                        <h4 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>{gap.title}</h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className="text-xs px-2 py-0.5 rounded"
                            style={
                              gap.source === 'adjuster_finding'
                                ? { backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }
                                : { backgroundColor: 'transparent', color: 'var(--color-verified)' }
                            }
                          >
                            {gap.source === 'adjuster_finding' ? 'Adjuster Finding' : 'Unclaimed Loss'}
                          </span>
                          {gap.confidence && (
                            <span
                              className="text-xs"
                              style={{
                                color:
                                  gap.confidence === 'HIGH' ? 'var(--color-verified)' :
                                  gap.confidence === 'MEDIUM' ? 'var(--color-brand)' : 'var(--color-needs-human-text)'
                              }}
                            >
                              {gap.confidence} confidence
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>+{formatCurrency(gap.if_addressed.value_increase)}</p>
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>+{gap.if_addressed.percentage_increase}%</p>
                    </div>
                  </div>

                  <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>{gap.description}</p>

                  <div className="rounded-md p-3 mb-3" style={{ backgroundColor: 'transparent' }}>
                    <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-verified)' }}>If Addressed:</p>
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>{formatCurrency(base_claim)}</span>
                      <ArrowRight className="w-4 h-4" style={{ color: 'var(--color-verified)' }} />
                      <span className="font-bold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>{formatCurrency(gap.cumulative_claim)}</span>
                    </div>
                  </div>

                  <div className="text-sm">
                    <p className="font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>How to Address:</p>
                    <p style={{ color: 'var(--color-text-secondary)' }}>{gap.how_to_address}</p>
                  </div>

                  {gap.evidence_needed && gap.evidence_needed.length > 0 && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--color-verified)' }}>
                      <p className="text-xs font-semibold mb-1" style={{ color: 'var(--color-text-secondary)' }}>Evidence Needed:</p>
                      <div className="flex flex-wrap gap-1">
                        {gap.evidence_needed.map((doc, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}>
                            {doc}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}

              {/* Summary Banner */}
              <div className="rounded-md p-4" style={{ backgroundColor: 'transparent', border: '1px solid var(--color-verified)' }}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--color-verified)' }}>Total Potential Recovery</p>
                    <p className="text-sm" style={{ color: 'var(--color-verified)' }}>If all {gap_bridge.length} gaps are addressed</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>+{formatCurrency(totalGapValue)}</p>
                    <p className="text-sm font-mono tabular-nums" style={{ color: 'var(--color-verified)' }}>
                      New Total: {base_claim == null ? NOT_COMPUTED_TEXT : formatMoneyOrNotComputed(base_claim + totalGapValue)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Monte Carlo Statistics */}
      {monte_carlo && (
        <div className="mb-6">
          <button
            onClick={() => toggleSection('monteCarlo')}
            className="w-full flex items-center justify-between p-4 rounded-md border border-[var(--color-border)] transition"
            style={{ backgroundColor: 'var(--color-bg-elevated)' }}
          >
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
              <span className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Monte Carlo Statistics ({monte_carlo.iterations.toLocaleString()} iterations)
              </span>
            </div>
            {expandedSections.monteCarlo ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expandedSections.monteCarlo && (
            <div className="mt-3 rounded-md border border-[var(--color-border)] p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="rounded-md border border-[var(--color-border)] p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Mean</p>
                  <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(monte_carlo.mean)}</p>
                </div>
                <div className="rounded-md border border-[var(--color-border)] p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Median (P50)</p>
                  <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(monte_carlo.median)}</p>
                </div>
                <div className="rounded-md border border-[var(--color-border)] p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Std Deviation</p>
                  <p className="text-lg font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>{formatCurrency(monte_carlo.std_dev)}</p>
                </div>
                <div className="rounded-md border border-[var(--color-border)] p-3 text-center" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Range</p>
                  <p className="text-sm font-bold font-mono tabular-nums" style={{ color: 'var(--color-needs-human-text)' }}>
                    {formatCurrency(monte_carlo.min)} - {formatCurrency(monte_carlo.max)}
                  </p>
                </div>
              </div>

              {/* Percentiles */}
              <div className="rounded-md border border-[var(--color-border)] p-4" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
                <p className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>Percentile Distribution</p>
                <div className="space-y-2">
                  {[
                    { label: 'P10 (Very Conservative)', value: monte_carlo.p10, color: 'var(--color-needs-human-tint-bd)' },
                    { label: 'P25 (Conservative)', value: monte_carlo.p25, color: 'var(--color-needs-human-tint-bd)' },
                    { label: 'P50 (Fair / Median)', value: monte_carlo.p50, color: 'var(--color-brand)' },
                    { label: 'P75 (Aggressive)', value: monte_carlo.p75, color: 'var(--color-brand)' },
                    { label: 'P90 (Very Aggressive)', value: monte_carlo.p90, color: 'var(--color-verified)' }
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-24 text-xs" style={{ color: 'var(--color-text-secondary)' }}>{item.label}</div>
                      <div className="flex-1 rounded-full h-6 relative" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
                        <div
                          className="h-full rounded-full"
                          style={{
                            backgroundColor: item.color,
                            width: `${((item.value - monte_carlo.min) / (monte_carlo.max - monte_carlo.min)) * 100}%`
                          }}
                        ></div>
                      </div>
                      <div className="w-24 text-right text-sm font-semibold font-mono tabular-nums" style={{ color: 'var(--color-text-secondary)' }}>
                        {formatCurrency(item.value)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Methodology Note */}
              <div className="mt-4 p-3 rounded-md border border-[var(--color-border)] text-xs" style={{ backgroundColor: 'var(--color-bg-surface)', color: 'var(--color-text-secondary)' }}>
                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Methodology:</strong> This Monte Carlo simulation runs {monte_carlo.iterations.toLocaleString()} iterations 
                    with multi-variable perturbation including revenue trend (±15%), seasonality factors (±10%), 
                    expense continuation rates (±20%), and recovery period assumptions (±15%). The P50 (median) 
                    represents the statistically most likely fair settlement value.
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info Banner */}
      <div className="rounded-md border border-[var(--color-border)] p-4" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 mt-0.5" style={{ color: 'var(--color-needs-human-text)' }} />
          <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            <p className="font-semibold mb-1">About Fair Settlement Calculator</p>
            <p>
              This analysis uses Monte Carlo simulation to find a statistically fair settlement value 
              between the adjuster's conservative position and the attorney's aggressive position. 
              The P50 (median) value represents where 50% of simulation outcomes fell above and below, 
              making it a defensible "middle ground" for mediation discussions.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
